import React, { useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { cleanAcademy, getPosts } from '@/store/actions/academy';
import { selectFilters, selectPosts } from '@/store/selectors/academy';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import ModalFilters from '@/components/academy/ModalFiltration';
import EmptyCatalog from '@/components/academy/EmptyCatalog';
import ItemHeader from '@/components/academy/ItemHeader';
import CreatePost from '@/components/academy/CreatePost';
import Catalog from '@/components/academy/Catalog';
import Loader from '@/components/common/Loader';
import Navigation from '@/components/Navigation';
import { useHistory } from 'react-router';
import { cleanUsers, getUsersWithManagers } from '@/store/actions/users';
import Empty from '@/components/Empty';

const Academy: React.FC = () => {
  const [isCreatePostActive, toggleIsCreatePostActive] = useToggle();
  const [isModalFiltersActive, toggleIsModalFiltersActive] = useToggle();
  const [{ common }] = useLanguage();
  const filters = useSelector(selectFilters);
  const posts = useSelector(selectPosts);
  const history = useHistory();

  const dispatch = useDispatch();

  // A layout effect, so the stored copies render before the first paint -- see
  // store/responses.ts.
  useLayoutEffect(() => {
    dispatch(getUsersWithManagers());
    dispatch(getPosts());
    return () => {
      if (!history.location.pathname.includes('academy')) {
        dispatch(cleanUsers());
        dispatch(cleanAcademy());
      }
    };
  }, []);

  // Derived in render, never copied into state by an effect: an effect runs with
  // the posts of the render that scheduled it, and the copy would outlive the
  // wipe that follows.
  const filteredPosts = useMemo(() => (posts && filters.length
    ? posts.filter(({ tags }) => filters.every((filter) => tags?.includes(filter)))
    : posts), [posts, filters]);

  if (isCreatePostActive) {
    return (
      <CreatePost
        hide={toggleIsCreatePostActive}
      />
    );
  }

  return (
    <>
      <ItemHeader
        headerName={common.lambda_edu}
        openAdd={toggleIsCreatePostActive}
        toggleFilter={toggleIsModalFiltersActive}
        isFilterActive={Boolean(filters.length)}
      />
      <Empty height="72px" />
      {!filteredPosts
        ? <Loader scale="0.5" />
        : (
          <ItemsContainer className="scrollbar">
            {filteredPosts.length ? (
              <Catalog
                posts={filteredPosts!}
              />
            ) : (
              <EmptyCatalog />
            )}
          </ItemsContainer>
        )}
      <Empty height="64px" />
      <Navigation path="academy" />
      {isModalFiltersActive && (
        <ModalFilters
          hideModal={toggleIsModalFiltersActive}
          posts={filteredPosts}
        />
      )}
    </>
  );
};

const ItemsContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  position: relative;
  padding: 16px 0;
  overflow-y: auto;
  flex: 1;
`;

export default Academy;
